import React from 'react';

/**
 * Collapse typographic punctuation variants to ASCII so surface-text matching is not
 * defeated by smart quotes/dashes. Every replacement is single-character so indices stay aligned.
 */
export function normalizeForMatch(text) {
    if (text == null) return '';
    return String(text)
        .replace(/\u2018/g, '\'').replace(/\u2019/g, '\'')
        .replace(/\u201A/g, '\'').replace(/\u2032/g, '\'')
        .replace(/\u201C/g, '"').replace(/\u201D/g, '"')
        .replace(/\u2013/g, '-').replace(/\u2014/g, '-')
        .replace(/\u00A0/g, ' ');
}

function isWordChar(ch) {
    return /[A-Za-z0-9_]/.test(ch);
}

/**
 * True when [start, end) is a whole token, not a substring inside a larger word.
 * Mirrors {@code EntitySnippetHighlighter.atWordBoundary} on the backend.
 */
function atWordBoundary(haystack, start, end, needle) {
    const leftOk = !isWordChar(needle.charAt(0))
        || start === 0
        || !isWordChar(haystack.charAt(start - 1));
    const rightOk = !isWordChar(needle.charAt(needle.length - 1))
        || end >= haystack.length
        || !isWordChar(haystack.charAt(end));
    return leftOk && rightOk;
}

function overlapsClaimed(claimed, start, end) {
    for (let i = start; i < end; i++) {
        if (claimed[i]) return true;
    }
    return false;
}

/**
 * Collect distinct surface forms for the entity being viewed, longest first so shorter
 * forms (e.g. "US") do not pre-empt longer ones ("the United States").
 */
export function collectSurfaceForms({
    canonicalName,
    aliases = [],
    surfaceForms = [],
    wikidataAliases = [],
} = {}) {
    const forms = new Set();
    const add = (value) => {
        const trimmed = String(value ?? '').trim();
        if (trimmed) forms.add(trimmed);
    };

    add(canonicalName);
    aliases.forEach(add);
    wikidataAliases.forEach(add);
    surfaceForms.forEach((form) => add(typeof form === 'string' ? form : form?.text));

    return [...forms].sort((a, b) => b.length - a.length);
}

/**
 * Project surface forms onto plain text, returning snippet-relative highlight spans.
 */
export function findEntitySpans(text = '', surfaceForms = []) {
    if (!text || !surfaceForms.length) return [];

    const haystack = normalizeForMatch(text).toLowerCase();
    const claimed = new Array(text.length).fill(false);
    const spans = [];

    for (const form of surfaceForms) {
        const needle = normalizeForMatch(form).toLowerCase();
        if (!needle) continue;

        let from = 0;
        while (from <= haystack.length - needle.length) {
            const idx = haystack.indexOf(needle, from);
            if (idx < 0) break;
            const end = idx + needle.length;
            if (atWordBoundary(haystack, idx, end, needle) && !overlapsClaimed(claimed, idx, end)) {
                for (let i = idx; i < end; i++) claimed[i] = true;
                spans.push({startOffset: idx, endOffset: end});
            }
            from = idx + 1;
        }
    }

    spans.sort((a, b) => a.startOffset - b.startOffset);
    return spans;
}

/**
 * Highlight matches of the currently-viewed entity in a plain-text KB snippet.
 * Styling matches the active-entity treatment in report preview / FINTEL claims tabs.
 */
export function highlightEntityInSnippet(text = '', entityContext = null) {
    if (!text || !entityContext) return text;

    const surfaceForms = collectSurfaceForms(entityContext);
    const spans = findEntitySpans(text, surfaceForms);
    if (!spans.length) return text;

    const result = [];
    let pos = 0;

    spans.forEach((span, idx) => {
        if (pos < span.startOffset) {
            result.push(text.slice(pos, span.startOffset));
        }
        result.push(
            <span
                key={`kb-entity-${idx}-${span.startOffset}`}
                className="wikidata-entity wikidata-entity--linked wikidata-entity--current"
            >
                {text.slice(span.startOffset, span.endOffset)}
            </span>,
        );
        pos = span.endOffset;
    });

    if (pos < text.length) {
        result.push(text.slice(pos));
    }

    return result;
}
